/**
 * SuperAdmin — Foundations Tab (Task 10.2)
 * Web only — ADR-014, CONSTRAINT 15 (foundationAdmin never in mobile RoleRouter).
 *
 * Two-panel layout:
 *   Left  — FoundationList: all foundations (GET /api/superadmin/foundations)
 *   Right — FoundationDetail: single foundation with affiliated centres + admin
 *           (GET /api/superadmin/foundations/:id, populated)
 *
 * Task 10.3: Broadcast panel added below affiliated centres card.
 *   POST /api/superadmin/foundations/:id/broadcast
 *   ConfirmModal required before send fires (CONSTRAINT 13 pattern).
 *
 * Task 10.4: Healer Network card added below affiliated centres card.
 *   GET /api/superadmin/foundations/:id/healers
 *   Aggregate view only — no individual names. byCourse + byCity breakdown.
 *
 * SB.2: Purple theme + MRR summary bar + status dots + country flags.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  FiAward, FiHome, FiMail, FiMapPin,
  FiChevronRight, FiLoader, FiRefreshCw, FiSend, FiUsers,
} from 'react-icons/fi';
import { getFoundations, getFoundationById, sendFoundationBroadcast, getFoundationHealers } from '../../utils/api';

const PLAN_LABELS = {
  FOUNDATION_STARTER:  'Starter',
  FOUNDATION_AI_ADMIN: 'AI Admin',
  ENTERPRISE:          'Enterprise',
  IIS_GRANT:           'IIS Grant',
};
const PLAN_COLORS = {
  FOUNDATION_STARTER:  '#2980b9',
  FOUNDATION_AI_ADMIN: '#8e44ad',
  ENTERPRISE:          '#e67e22',
  IIS_GRANT:           '#d4ac0d',
};
const TYPE_COLORS = {
  foundation:   '#1a6b5a',
  institute:    '#2980b9',
  organization: '#8e44ad',
};

// SB.2 — Foundation purple theme tokens
const F_PRIMARY = '#7C3AED';
const F_LIGHT   = '#EDE9FE';
const F_DARK    = '#5B21B6';

// Returns Unicode flag emoji for a 2-letter ISO country code.
function countryFlag(code) {
  if (!code || code.length !== 2) return '';
  return [...code.toUpperCase()]
    .map(c => String.fromCodePoint(0x1F1E0 + c.charCodeAt(0) - 65))
    .join('');
}

// Formats a number as ₹ en-IN currency with no decimal places.
function fmtMRR(amount) {
  if (!amount) return null;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Responsive helpers ───────────────────────────────────────────────────
const STYLE_ID = 'sa-foundations-responsive';
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @media (max-width: 768px) {
      .sa-found-panels { flex-direction: column !important; }
      .sa-found-list   { width: 100% !important; max-height: 280px !important;
                         border-right: none !important; border-bottom: 1px solid #e8eae8 !important; }
    }
    @keyframes sa-found-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(el);
}

// ─── Styles ───────────────────────────────────────────────────────────────
const S = {
  panels:      { display: 'flex', background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', minHeight: '500px' },
  listPanel:   { width: '300px', flexShrink: 0, borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', overflowY: 'auto' },
  listHeader:  { padding: '16px 16px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  listTitle:   { fontSize: '12px', fontWeight: '700', color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.5px' },
  countBadge:  { fontSize: '10px', fontWeight: '700', borderRadius: '9px', padding: '2px 7px', background: 'rgba(26,107,90,0.10)', color: '#1a6b5a' },
  listRow:    (active) => ({ padding: '13px 16px', borderBottom: '1px solid #f6f6f6', cursor: 'pointer', background: active ? F_LIGHT : 'transparent', borderLeft: active ? `3px solid ${F_PRIMARY}` : '3px solid transparent', transition: 'background 0.15s' }),
  rowName:     { fontSize: '13px', fontWeight: '600', color: '#1a1a2e' },
  rowMeta:     { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  pill:       (color) => ({ fontSize: '10px', fontWeight: '700', borderRadius: '4px', padding: '1px 6px', background: `${color}18`, color }),
  detailPanel: { flex: 1, padding: '20px', overflowY: 'auto', background: '#fafafa' },
  emptyState:  { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: '#bbb', gap: '10px' },
  card:        { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: '14px' },
  cardHeader:  { padding: '13px 18px 11px', borderBottom: '1px solid #f0f0f0', fontSize: '12px', fontWeight: '700', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.4px' },
  fieldRow:    { display: 'flex', gap: '12px', padding: '10px 18px', borderBottom: '1px solid #f8f8f8', alignItems: 'flex-start' },
  fieldLabel:  { width: '110px', flexShrink: 0, fontSize: '11px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.3px', paddingTop: '1px' },
  fieldValue:  { fontSize: '13px', color: '#1a1a2e', flex: 1, lineHeight: '1.4' },
  centreRow:   { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', borderBottom: '1px solid #f8f8f8' },
  centreIcon:  { width: '28px', height: '28px', borderRadius: '7px', background: 'rgba(26,107,90,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '13px', color: '#1a6b5a' },
  centreName:  { fontSize: '13px', fontWeight: '600', color: '#1a1a2e', flex: 1 },
  centreCity:  { fontSize: '11px', color: '#999' },
  activeBadge: (active) => ({ fontSize: '10px', fontWeight: '700', borderRadius: '4px', padding: '2px 6px', background: active ? 'rgba(39,174,96,0.10)' : 'rgba(150,150,150,0.12)', color: active ? '#27ae60' : '#999' }),
  createBtn:   { background: '#4CE3CD', color: '#0D0F1F', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '28px' },
  netRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 18px', borderBottom: '1px solid #f8f8f8' },
  netLabel:    { fontSize: '12px', color: '#444' },
  netCount:    { fontSize: '12px', fontWeight: '700', color: '#2BB8A4' },
  netSection:  { padding: '8px 18px 4px', fontSize: '10px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.4px' },
  // SB.2 — MRR Finance Panel
  summaryBar:  { display: 'flex', background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '12px', overflow: 'hidden' },
  statCard:    { flex: 1, padding: '14px 16px', textAlign: 'center' },
  statValue:   { fontSize: '20px', fontWeight: '800', color: F_PRIMARY, lineHeight: '1.2' },
  statLabel:   { fontSize: '10px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '3px' },
  statDivider: { width: '1px', background: '#f0f0f0', flexShrink: 0 },
};

// ─── Shared sub-views ─────────────────────────────────────────────────────
const SpinnerView = ({ label = 'Loading…' }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '10px', color: '#888' }}>
    <FiLoader style={{ fontSize: '18px', animation: 'sa-found-spin 1s linear infinite' }} />
    <span style={{ fontSize: '13px' }}>{label}</span>
  </div>
);

const RetryView = ({ message, onRetry }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 20px', gap: '12px', textAlign: 'center' }}>
    <div style={{ fontSize: '12px', color: '#e74c3c', maxWidth: '260px', lineHeight: '1.5' }}>{message}</div>
    <button type="button" onClick={onRetry} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '7px', border: 'none', background: '#1a6b5a', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
      <FiRefreshCw style={{ fontSize: '12px' }} /> Retry
    </button>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────
const Foundations = () => {
  const [foundations,   setFoundations]   = useState([]);
  const [listLoading,   setListLoading]   = useState(true);
  const [listError,     setListError]     = useState(null);
  const [selectedId,    setSelectedId]    = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState(null);

  const [broadcastChannel,    setBroadcastChannel]    = useState('both');
  const [broadcastSubject,    setBroadcastSubject]     = useState('');
  const [broadcastMessage,    setBroadcastMessage]     = useState('');
  const [broadcastConfirm,    setBroadcastConfirm]     = useState(false);
  const [broadcastSubmitting, setBroadcastSubmitting]  = useState(false);
  const [broadcastResult,     setBroadcastResult]      = useState(null);
  const [broadcastError,      setBroadcastError]       = useState(null);

  const [healerNetwork,        setHealerNetwork]        = useState(null);
  const [healerNetworkLoading, setHealerNetworkLoading] = useState(false);
  const [healerNetworkError,   setHealerNetworkError]   = useState(null);

  const selectedIdRef = useRef(null);

  // SB.2 — derived summary stats from the loaded foundations list.
  // mrr and lastActivity fields silently hidden if backend doesn't return them.
  const summary = useMemo(() => ({
    count:        foundations.length,
    totalCentres: foundations.reduce((s, f) => s + (f.affiliatedTenants?.length || 0), 0),
    totalMRR:     foundations.reduce((s, f) => s + (f.mrr || 0), 0),
    activeSubs:   foundations.filter(f => f.status === 'active').length,
  }), [foundations]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await getFoundations();
      setFoundations(res.data.data || []);
    } catch (err) {
      setListError(err.response?.data?.message || 'Failed to load foundations.');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const selectFoundation = useCallback(async (id) => {
    if (id === selectedId) return;
    const targetId = id;
    setSelectedId(id);
    selectedIdRef.current = id;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    setBroadcastSubject('');
    setBroadcastMessage('');
    setBroadcastChannel('both');
    setBroadcastResult(null);
    setBroadcastError(null);
    setHealerNetwork(null);
    setHealerNetworkError(null);
    setHealerNetworkLoading(true);

    const [detailResult, healersResult] = await Promise.allSettled([
      getFoundationById(id),
      getFoundationHealers(id),
    ]);

    if (targetId !== selectedIdRef.current) return;

    if (detailResult.status === 'fulfilled') {
      setDetail(detailResult.value.data.data);
    } else {
      setDetailError(detailResult.reason?.response?.data?.message || 'Failed to load foundation details.');
    }
    setDetailLoading(false);

    if (healersResult.status === 'fulfilled') {
      setHealerNetwork(healersResult.value.data.data);
    } else {
      setHealerNetworkError(healersResult.reason?.response?.data?.message || 'Failed to load healer network.');
    }
    setHealerNetworkLoading(false);
  }, [selectedId]);

  const retryHealerNetwork = useCallback(async () => {
    if (!selectedId) return;
    setHealerNetworkError(null);
    setHealerNetworkLoading(true);
    try {
      const res = await getFoundationHealers(selectedId);
      setHealerNetwork(res.data.data);
    } catch (err) {
      setHealerNetworkError(err.response?.data?.message || 'Failed to load healer network.');
    } finally {
      setHealerNetworkLoading(false);
    }
  }, [selectedId]);

  const handleBroadcast = useCallback(async () => {
    setBroadcastSubmitting(true);
    setBroadcastError(null);
    try {
      const res = await sendFoundationBroadcast(selectedId, {
        subject: broadcastSubject,
        message: broadcastMessage,
        channel: broadcastChannel,
      });
      setBroadcastResult(res.data.data);
      setBroadcastConfirm(false);
    } catch (err) {
      setBroadcastError(err.response?.data?.message || 'Broadcast failed. Please try again.');
      setBroadcastConfirm(false);
    } finally {
      setBroadcastSubmitting(false);
    }
  }, [selectedId, broadcastSubject, broadcastMessage, broadcastChannel, setBroadcastConfirm]);

  return (
    <>
      {/* SB.2 — MRR Finance Panel: derived from loaded list; hidden while loading or empty */}
      {!listLoading && !listError && foundations.length > 0 && (
        <div style={S.summaryBar}>
          <div style={S.statCard}>
            <div style={S.statValue}>{summary.count}</div>
            <div style={S.statLabel}>Foundations</div>
          </div>
          <div style={S.statDivider} />
          <div style={S.statCard}>
            <div style={S.statValue}>{summary.totalCentres}</div>
            <div style={S.statLabel}>Total Centres</div>
          </div>
          <div style={S.statDivider} />
          <div style={S.statCard}>
            <div style={S.statValue}>{summary.activeSubs}</div>
            <div style={S.statLabel}>Active</div>
          </div>
          {summary.totalMRR > 0 && (
            <>
              <div style={S.statDivider} />
              <div style={S.statCard}>
                <div style={S.statValue}>{fmtMRR(summary.totalMRR)}</div>
                <div style={S.statLabel}>Combined MRR</div>
              </div>
            </>
          )}
        </div>
      )}

      <div style={S.panels} className="sa-found-panels">

        {/* ── Left: FoundationList ─────────────────────── */}
        <div style={S.listPanel} className="sa-found-list">
          <div style={S.listHeader}>
            <span style={S.listTitle}>Foundations</span>
            {!listLoading && !listError && <span style={S.countBadge}>{foundations.length}</span>}
          </div>

          {listLoading && <SpinnerView label="Loading foundations…" />}
          {listError   && <RetryView message={listError} onRetry={fetchList} />}
          {!listLoading && !listError && foundations.length === 0 && (
            <div style={{ padding: '24px 16px', fontSize: '12px', color: '#bbb', textAlign: 'center' }}>
              No foundations yet.
            </div>
          )}
          {!listLoading && !listError && foundations.map(f => (
            <div
              key={f._id}
              style={S.listRow(selectedId === f._id)}
              onClick={() => selectFoundation(f._id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && selectFoundation(f._id)}
            >
              {/* SB.2 — status dot + country flag + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                <span style={{
                  width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                  background: f.status === 'active' ? F_PRIMARY : '#ccc',
                }} />
                <span style={S.rowName}>
                  {countryFlag(f.countryCode)}{countryFlag(f.countryCode) ? ' ' : ''}{f.name}
                </span>
              </div>
              {/* SB.2 — purple plan pill + centre count + MRR + last activity */}
              <div style={S.rowMeta}>
                <span style={{ fontSize: '10px', fontWeight: '700', borderRadius: '4px', padding: '1px 6px', background: F_LIGHT, color: F_DARK }}>
                  {PLAN_LABELS[f.plan] || f.plan}
                </span>
                {f.affiliatedTenants?.length > 0 && (
                  <span style={{ fontSize: '10px', color: '#999' }}>
                    {f.affiliatedTenants.length} centre{f.affiliatedTenants.length !== 1 ? 's' : ''}
                  </span>
                )}
                {f.mrr > 0 && (
                  <span style={{ fontSize: '10px', color: F_PRIMARY, fontWeight: '600' }}>
                    {fmtMRR(f.mrr)}
                  </span>
                )}
                {f.lastActivity && (
                  <span style={{ fontSize: '10px', color: '#bbb' }}>
                    {new Date(f.lastActivity).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── Right: FoundationDetail ───────────────────── */}
        <div style={S.detailPanel}>
          {!selectedId && (
            <div style={S.emptyState}>
              <FiAward style={{ fontSize: '32px', color: '#ddd' }} />
              <span style={{ fontSize: '13px' }}>Select a foundation to view details</span>
            </div>
          )}

          {selectedId && detailLoading && <SpinnerView label="Loading details…" />}
          {selectedId && detailError   && <RetryView message={detailError} onRetry={() => selectFoundation(selectedId)} />}

          {selectedId && !detailLoading && !detailError && detail && (
            <>
              {/* Overview card */}
              <div style={S.card}>
                <div style={S.cardHeader}><FiAward /> Foundation Overview</div>
                <div style={S.fieldRow}><span style={S.fieldLabel}>Name</span><span style={S.fieldValue}>{detail.name}</span></div>
                <div style={S.fieldRow}><span style={S.fieldLabel}>Type</span><span style={S.fieldValue}><span style={S.pill(TYPE_COLORS[detail.type] || '#888')}>{detail.type}</span></span></div>
                <div style={S.fieldRow}><span style={S.fieldLabel}>Country</span><span style={S.fieldValue}>{detail.countryCode}{detail.city ? ` — ${detail.city}` : ''}</span></div>
                <div style={S.fieldRow}><span style={S.fieldLabel}>Status</span><span style={S.fieldValue}><span style={S.activeBadge(detail.status === 'active')}>{detail.status}</span></span></div>
                {detail.description && (
                  <div style={S.fieldRow}><span style={S.fieldLabel}>About</span><span style={{ ...S.fieldValue, color: '#555' }}>{detail.description}</span></div>
                )}
              </div>

              {/* Admin card */}
              <div style={S.card}>
                <div style={S.cardHeader}><FiMail /> Foundation Admin</div>
                <div style={S.fieldRow}>
                  <span style={S.fieldLabel}>Name</span>
                  <span style={S.fieldValue}>{detail.foundationAdminId?.firstName} {detail.foundationAdminId?.lastName}</span>
                </div>
                <div style={S.fieldRow}>
                  <span style={S.fieldLabel}>Email</span>
                  <span style={S.fieldValue}>
                    <a href={`mailto:${detail.foundationAdminId?.email}`}
                       style={{ color: '#1a6b5a', textDecoration: 'none', fontSize: '13px' }}>
                      {detail.foundationAdminId?.email}
                    </a>
                  </span>
                </div>
              </div>

              {/* Plan card */}
              <div style={S.card}>
                <div style={S.cardHeader}><FiChevronRight /> Plan & Billing</div>
                <div style={S.fieldRow}><span style={S.fieldLabel}>Plan</span><span style={S.fieldValue}><span style={S.pill(PLAN_COLORS[detail.plan] || '#888')}>{PLAN_LABELS[detail.plan] || detail.plan}</span></span></div>
                <div style={S.fieldRow}><span style={S.fieldLabel}>Billing</span><span style={S.fieldValue}>{detail.billingType?.replace(/_/g, ' ')}</span></div>
                <div style={S.fieldRow}><span style={S.fieldLabel}>Max centres</span><span style={S.fieldValue}>{detail.maxCentres === 0 ? 'Unlimited' : detail.maxCentres}</span></div>
                {detail.isIISGrant && (
                  <div style={S.fieldRow}><span style={S.fieldLabel}>IIS Grant</span><span style={{ ...S.fieldValue, color: '#d4ac0d', fontWeight: '700' }}>Yes — strategic grant</span></div>
                )}
              </div>

              {/* Affiliated centres card */}
              <div style={S.card}>
                <div style={S.cardHeader}>
                  <FiHome /> Affiliated Centres
                  <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: '#aaa' }}>
                    {detail.affiliatedTenants?.length || 0} centre{detail.affiliatedTenants?.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {(!detail.affiliatedTenants || detail.affiliatedTenants.length === 0)
                  ? <div style={{ padding: '16px 18px', fontSize: '12px', color: '#bbb' }}>No centres affiliated yet.</div>
                  : detail.affiliatedTenants.map(t => (
                      <div key={t._id} style={S.centreRow}>
                        <div style={S.centreIcon}><FiHome /></div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.centreName}>{t.tenantName}</div>
                          {t.city && (
                            <div style={S.centreCity}>
                              <FiMapPin style={{ fontSize: '10px', marginRight: '3px' }} />{t.city}
                            </div>
                          )}
                        </div>
                        <span style={S.activeBadge(t.isActive)}>{t.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                    ))
                }
              </div>

              {/* Healer Network card — Task 10.4, aggregate view only, no individual names */}
              <div style={S.card}>
                <div style={S.cardHeader}>
                  <FiUsers /> Healer Network
                  {healerNetwork && (
                    <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: '#aaa' }}>
                      {healerNetwork.totalHealers} healer{healerNetwork.totalHealers !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {healerNetworkLoading && <SpinnerView label="Loading healer network…" />}
                {healerNetworkError && (
                  <RetryView message={healerNetworkError} onRetry={retryHealerNetwork} />
                )}

                {!healerNetworkLoading && !healerNetworkError && healerNetwork && (
                  healerNetwork.totalHealers === 0
                    ? <div style={{ padding: '16px 18px', fontSize: '12px', color: '#bbb' }}>No active healers in affiliated centres.</div>
                    : <>
                        {Object.keys(healerNetwork.byCourse).length > 0 && (
                          <>
                            <div style={S.netSection}>By Course</div>
                            {Object.entries(healerNetwork.byCourse)
                              .sort((a, b) => b[1] - a[1])
                              .map(([course, count]) => (
                                <div key={course} style={S.netRow}>
                                  <span style={S.netLabel}>{course}</span>
                                  <span style={S.netCount}>{count}</span>
                                </div>
                              ))
                            }
                          </>
                        )}
                        {Object.keys(healerNetwork.byCourse).length > 0 && Object.keys(healerNetwork.byCity).length > 0 && (
                          <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />
                        )}
                        {Object.keys(healerNetwork.byCity).length > 0 && (
                          <>
                            <div style={S.netSection}>By City</div>
                            {Object.entries(healerNetwork.byCity)
                              .sort((a, b) => b[1] - a[1])
                              .map(([city, count]) => (
                                <div key={city} style={S.netRow}>
                                  <span style={S.netLabel}>
                                    <FiMapPin style={{ fontSize: '10px', marginRight: '3px', color: '#999' }} />
                                    {city}
                                  </span>
                                  <span style={S.netCount}>{count}</span>
                                </div>
                              ))
                            }
                          </>
                        )}
                      </>
                )}
              </div>

              {/* Broadcast card — only shown when there are affiliated centres */}
              {detail.affiliatedTenants?.length > 0 && (
                <div style={S.card}>
                  <div style={S.cardHeader}><FiSend /> Broadcast to Affiliated Centres</div>
                  <div style={{ padding: '16px 18px' }}>

                    {/* Channel selector */}
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '8px' }}>Channel</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {['email', 'whatsapp', 'both'].map(ch => (
                          <button key={ch} type="button"
                            onClick={() => { setBroadcastChannel(ch); setBroadcastResult(null); setBroadcastError(null); }}
                            style={{ padding: '7px 14px', borderRadius: '6px', border: `1px solid ${broadcastChannel === ch ? '#1a6b5a' : '#e0e0e0'}`, background: broadcastChannel === ch ? 'rgba(26,107,90,0.08)' : '#fff', color: broadcastChannel === ch ? '#1a6b5a' : '#555', fontSize: '12px', fontWeight: '600', cursor: 'pointer', minHeight: '36px', textTransform: 'capitalize' }}>
                            {ch}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Subject — hidden for whatsapp-only */}
                    {broadcastChannel !== 'whatsapp' && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>Subject</div>
                        <input type="text" value={broadcastSubject} onChange={e => setBroadcastSubject(e.target.value)}
                          placeholder="Email subject line"
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: '7px', fontSize: '13px', color: '#1a1a2e', outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                    )}

                    {/* Message */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>Message</div>
                      <textarea value={broadcastMessage} onChange={e => setBroadcastMessage(e.target.value)}
                        placeholder="Write your message to all affiliated centres…"
                        rows={4}
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: '7px', fontSize: '13px', color: '#1a1a2e', resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    </div>

                    {/* Result / Error feedback */}
                    {broadcastResult && (
                      <div style={{ padding: '10px 14px', background: 'rgba(39,174,96,0.08)', borderRadius: '7px', border: '1px solid rgba(39,174,96,0.2)', fontSize: '12px', color: '#27ae60', marginBottom: '12px' }}>
                        Sent — Email: {broadcastResult.sent.email.sent}/{broadcastResult.sent.email.attempted} · WhatsApp: {broadcastResult.sent.whatsapp.attempted} attempted
                        {broadcastResult.note && <div style={{ color: '#888', marginTop: '4px' }}>{broadcastResult.note}</div>}
                      </div>
                    )}
                    {broadcastError && (
                      <div style={{ padding: '10px 14px', background: '#fdecea', borderRadius: '7px', border: '1px solid #e74c3c', fontSize: '12px', color: '#c0392b', marginBottom: '12px' }}>
                        {broadcastError}
                      </div>
                    )}

                    {/* Send button — disabled until required fields filled */}
                    {(() => {
                      const needsSubject = broadcastChannel === 'email' || broadcastChannel === 'both';
                      const disabled = broadcastSubmitting || !broadcastMessage.trim() || (needsSubject && !broadcastSubject.trim());
                      return (
                        <button type="button" onClick={() => setBroadcastConfirm(true)} disabled={disabled}
                          style={{ width: '100%', padding: '11px', borderRadius: '8px', border: 'none', background: disabled ? '#ccc' : '#1a6b5a', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '44px' }}>
                          {broadcastSubmitting ? 'Sending…' : `Send Broadcast to ${detail.affiliatedTenants.length} Centre${detail.affiliatedTenants.length !== 1 ? 's' : ''}`}
                        </button>
                      );
                    })()}

                    {(broadcastChannel === 'whatsapp' || broadcastChannel === 'both') && (
                      <div style={{ fontSize: '11px', color: '#aaa', marginTop: '8px', textAlign: 'center' }}>WhatsApp delivery pending AiSensy template approval</div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes — only rendered if present */}
              {detail.notes && (
                <div style={S.card}>
                  <div style={S.cardHeader}>Notes</div>
                  <div style={{ padding: '14px 18px', fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                    {detail.notes}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </div>{/* end S.panels */}

      {/* ConfirmModal — fixed overlay, outside panels div */}
      {broadcastConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px 28px 24px', maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>Confirm Broadcast</div>
            <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '24px' }}>
              Send broadcast to <strong>{detail?.affiliatedTenants?.length || 0} centre{(detail?.affiliatedTenants?.length || 0) !== 1 ? 's' : ''}</strong> via <strong>{broadcastChannel}</strong>? This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setBroadcastConfirm(false)}
                style={{ padding: '9px 18px', borderRadius: '7px', border: '1px solid #e0e0e0', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
                Cancel
              </button>
              <button type="button" onClick={handleBroadcast} disabled={broadcastSubmitting}
                style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: broadcastSubmitting ? '#aaa' : '#1a6b5a', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: broadcastSubmitting ? 'not-allowed' : 'pointer', minHeight: '44px' }}>
                {broadcastSubmitting ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Foundations;
